import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { formatDate } from "utils";

import { useDebouncedSave } from "./useDebouncedSave";
import { useSaveCommentDraft } from "./useSaveCommentDraft";

export function useAutoSaver(
  comment,
  content,
  isLoading,
  draftGid,
  setDraftGid,
  taskUuid,
  clientWorkflowUuid
) {
  const { clientId } = useParams();

  const previousUpdatedAt = comment?.draft?.updatedAt || comment?.updatedAt;

  const { saveDraft, updatedAt, savingErrors, isSavingDraft } =
    useSaveCommentDraft(
      clientId,
      comment && comment.commentType === "NORMAL" ? comment.id : null
    );

  const debouncedSave = useDebouncedSave(saveDraft, setDraftGid);
  useEffect(() => {
    debouncedSave(content, draftGid, taskUuid, clientWorkflowUuid, isLoading);
  }, [
    content,
    isLoading,
    debouncedSave,
    draftGid,
    taskUuid,
    clientWorkflowUuid,
  ]);

  const latestUpdatedAt = updatedAt || previousUpdatedAt;

  return {
    saveDraft: saveDraft,
    cancelDelayedSaving: debouncedSave.cancel,
    isSavingDraft: isSavingDraft,
    message: useMemo(() => {
      if (isSavingDraft) return "Saving...";
      if (savingErrors?.length) return "Error saving draft";
      if (latestUpdatedAt)
        return `Last saved on: ${formatDate("simpleDateTime")(
          new Date(latestUpdatedAt)
        )}`;
      return "Not yet saved";
    }, [isSavingDraft, savingErrors, latestUpdatedAt]),
  };
}

export default useAutoSaver;
